import type { AIChatAnswer, AIChatRequest, AIProvider, AIReviewRequest } from "../../domain/ai-review/ai-provider.js";
import { applyLanguageSuggestions, reviewLanguage } from "../../domain/ai-review/language-rules.js";
import { coherenceFindings, normFindings, terminologyFindings, unitFindings, type ReviewField } from "../../domain/ai-review/technical-rules.js";
import { NO_KNOWLEDGE_SUPPORT } from "../../domain/ai-review/prompt-templates.js";

/**
 * INSPECT IA — MOTOR LOCAL DE REGRAS.
 *
 * Provedor determinístico: português (regras seguras), terminologia,
 * coerência resultados × conclusão, normas e unidades. Não chama serviço
 * externo, não inventa texto técnico e sempre responde igual para a mesma
 * entrada. Substituível por um provedor LLM no servidor (mesma interface),
 * e a saída continua passando pela validação de esquema e pela proteção de
 * valores do orquestrador.
 *
 * Substitui o `MockProvider` do Córtex, que devolvia textos fabricados
 * ("PMTA de 10,0 kgf/cm²", "ASME PCC-1") sem relação com o relatório.
 */
export class LocalRulesProvider implements AIProvider {
  readonly id = "INSPECT_IA_LOCAL";
  readonly model = "regras-ptbr";
  readonly modelVersion = "1.0.0";

  async isAvailable(): Promise<boolean> {
    return true;
  }

  async review(request: AIReviewRequest): Promise<unknown> {
    const findings: Array<Record<string, unknown>> = [];
    for (const field of request.fields) {
      if (!field.text || !field.text.trim()) continue;
      for (const s of reviewLanguage(field.text)) {
        findings.push({
          fieldPath: field.fieldPath, category: s.category, severity: "LINGUISTICO",
          originalText: s.originalText, suggestedText: s.suggestedText, explanation: s.explanation,
          autoApplicable: true, ruleId: s.ruleId, offset: s.offset ?? -1
        });
      }
      for (const t of [...terminologyFindings(field), ...normFindings(field, request.facts, request.knowledgeDocuments), ...unitFindings(field)]) {
        findings.push({ ...t, autoApplicable: false, offset: field.text.indexOf(t.originalText) });
      }
    }
    for (const c of coherenceFindings(request.fields, request.facts)) {
      findings.push({ ...c, autoApplicable: false, offset: -1 });
    }
    return { findings };
  }

  async chat(request: AIChatRequest): Promise<AIChatAnswer> {
    const q = request.question.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const conclusive = request.fields.filter(f => f.conclusive);

    // "Qual informação falta?" — o validador oficial do módulo responde.
    if (/\b(falta|faltam|faltando|pendenc|incomplet)/.test(q)) {
      if (!request.subjectLabel) return plain("Abra o assistente dentro de um relatório para eu verificar o que falta nele.");
      const emptyText = request.fields.filter(f => f.writable !== false && !f.text.trim()).map(f => f.label);
      const lines = [
        ...request.modulePendencies.map(p => `• ${p}`),
        ...(emptyText.length ? [`• Campos de texto ainda vazios: ${emptyText.join(", ")}.`] : [])
      ];
      return plain(lines.length ? `Pendências do ${request.subjectLabel}, pelo validador do próprio módulo:\n${lines.join("\n")}` : `O validador do módulo não aponta pendência no ${request.subjectLabel}.`);
    }

    // "Revise minha conclusão" / "essa recomendação está coerente?" — mesmas regras da revisão.
    if (/\b(revis|coeren|conclus|recomendac)/.test(q) && request.fields.length > 0) {
      const target = pickField(q, request.fields, conclusive);
      if (!target) return plain("Não encontrei o campo a revisar neste relatório.");
      const lang = reviewLanguage(target.text);
      const issues = [
        ...coherenceFindings([{ ...target, conclusive: true }], request.facts ?? { measurements: [], results: [], recommendationsCount: null, declaredReferences: [], dates: [], periods: [] }),
        ...terminologyFindings(target)
      ];
      const parts: string[] = [];
      parts.push(issues.length ? `Alertas em "${target.label}":\n${issues.map(i => `• ${i.explanation}`).join("\n")}` : `Não encontrei incoerência entre "${target.label}" e os resultados registrados.`);
      parts.push(lang.length ? `Correções de português propostas: ${lang.length}.` : "Nenhuma correção de português necessária.");
      return {
        text: parts.join("\n\n"), sourceRefs: [], grounded: true,
        fieldSuggestion: lang.length && target.writable !== false ? { fieldPath: target.fieldPath, label: target.label, text: applyLanguageSuggestions(target.text, lang), baseText: target.text } : null
      };
    }

    // Demais perguntas: só o que a Base Técnica sustenta, citado literalmente.
    if (request.knowledge.length === 0) return plain(NO_KNOWLEDGE_SUPPORT, false);
    const text = request.knowledge.map(h => `${h.entry.documentCode}${h.entry.revision ? ` (Rev. ${h.entry.revision})` : ""}, ${h.entry.section}:\n«${h.excerpt}»`).join("\n\n");
    return {
      text: `Trechos da Base Técnica relacionados à pergunta:\n\n${text}\n\nA interpretação para este caso é do profissional responsável.`,
      sourceRefs: request.knowledge.map(h => ({ document: h.entry.documentCode, revision: h.entry.revision, section: h.entry.section, entryId: h.entry.id })),
      fieldSuggestion: null,
      grounded: true
    };
  }
}

const plain = (text: string, grounded = true): AIChatAnswer => ({ text, sourceRefs: [], fieldSuggestion: null, grounded });

function pickField(q: string, fields: readonly ReviewField[], conclusive: readonly ReviewField[]): ReviewField | null {
  if (/recomendac/.test(q)) {
    const rec = fields.find(f => /recomend/i.test(f.label) && f.text.trim());
    if (rec) return rec;
  }
  return conclusive.find(f => f.text.trim()) ?? fields.find(f => f.text.trim()) ?? null;
}
